import parse, { domToReact } from "html-react-parser";
import Link from "next/link";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css"; // Import a theme (you can choose different themes)
import "prismjs/components/prism-javascript"; // Import language support
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-css";
import "prismjs/components/prism-python";
// Add more languages as needed

/**
 * Check if a URL is an internal link (same domain or relative path)
 */
const isInternalLink = (href) => {
  if (!href) return false;

  // Relative paths are internal
  if (href.startsWith("/")) return true;

  // Check if it's our domain
  try {
    const url = new URL(href);
    const ourDomains = [
      "sunhivemicroschool.org",
      "www.sunhivemicroschool.org",
      "localhost",
    ];
    return ourDomains.some((domain) => url.hostname.includes(domain));
  } catch {
    // If URL parsing fails, treat as internal if no protocol
    return !href.includes("://");
  }
};

/**
 * Get the pathname from a URL (for internal links)
 */
const getInternalPath = (href) => {
  if (href.startsWith("/")) return href;

  try {
    const url = new URL(href);
    return url.pathname + url.search + url.hash;
  } catch {
    return href;
  }
};

/**
 * Parser options to replace internal <a> tags with Next.js <Link>
 */
const parserOptions = {
  replace: (domNode) => {
    // Only process anchor elements
    if (domNode.name === "a" && domNode.attribs) {
      const href = domNode.attribs.href;
      const target = domNode.attribs.target;

      // Only convert internal links that don't open in new tab
      if (href && isInternalLink(href) && target !== "_blank") {
        const internalPath = getInternalPath(href);
        const children = domNode.children
          ? domToReact(domNode.children, parserOptions)
          : null;

        return (
          <Link
            href={internalPath}
            style={{
              color: "inherit",
              textDecoration: domNode.attribs.style?.includes("text-decoration")
                ? undefined
                : "underline",
            }}
          >
            {children}
          </Link>
        );
      }
    }
    // Return undefined to keep the original element
    return undefined;
  },
};

export const highlightCode = (htmlContent) => {
  if (!htmlContent) return null;
  if (typeof document !== "undefined") {
    Prism.highlightAll();
  }

  // Parse the HTML content
  let processedContent = htmlContent;

  // Replace the custom-code-block class with prism classes (if any custom classes are used)
  processedContent = processedContent.replace(
    /<pre class="custom-code-block"><code>/g,
    '<pre><code class="language-javascript">',
  );

  // Parse the content with React, converting internal links to Next.js Link
  const highlightedContent = parse(processedContent, parserOptions);

  // Apply Prism highlighting
  setTimeout(() => {
    Prism.highlightAll();
  }, 0);

  return highlightedContent;
};
